export type Provider = 'google' | 'microsoft' | 'apple'

/** What login/signup resolves to: signed in, or a code was emailed for verification. */
export type AuthOutcome =
  | { status: 'authenticated' }
  | { status: 'verification_required'; pendingToken: string; email: string }

/** The shape of any auth-endpoint JSON body (all fields optional). */
interface AuthResponseBody {
  status?: string
  error?: string
  pendingToken?: string
  email?: string
}

interface PostResult {
  status: number
  data: AuthResponseBody
}

async function postJson(path: string, body: unknown): Promise<PostResult> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  const data = (await res.json().catch(() => ({}))) as AuthResponseBody
  return { status: res.status, data }
}

function outcomeOrThrow(res: PostResult): AuthOutcome {
  if (res.status >= 400) {
    throw new Error(res.data.error ?? 'Something went wrong.')
  }
  if (res.data.status === 'verification_required' && res.data.pendingToken && res.data.email) {
    return {
      status: 'verification_required',
      pendingToken: res.data.pendingToken,
      email: res.data.email,
    }
  }
  return { status: 'authenticated' }
}

/** Email/password login. May resolve to a verification step. */
export async function login(email: string, password: string): Promise<AuthOutcome> {
  return outcomeOrThrow(await postJson('/api/auth/login', { email, password }))
}

/** Create an account. New accounts resolve to a verification step. */
export async function signUp(email: string, password: string): Promise<AuthOutcome> {
  return outcomeOrThrow(await postJson('/api/auth/register', { email, password }))
}

/** Submit the emailed verification code to finish signing in. */
export async function verifyEmail(pendingToken: string, code: string): Promise<void> {
  const res = await postJson('/api/auth/verify', { pendingToken, code })
  if (res.status >= 400) {
    throw new Error(res.data.error ?? 'Verification failed.')
  }
}

/** Start real WorkOS OAuth for a social provider (full-page redirect). */
export function signInWithProvider(provider: Provider): void {
  window.location.href = `/api/auth/sso?provider=${provider}`
}

/** Clears the session server-side, then returns to the login screen. */
export async function signOut(): Promise<void> {
  // JSON content-type is required by the endpoint's CSRF guard, even with no body.
  await fetch('/api/auth/logout', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: '{}',
  })
  window.location.href = '/login'
}
